import React, { useMemo } from 'react'
import { InputNumber, Row, Col } from 'antd'
import './DurationPicker.scss'

interface DurationPickerProps {
  value: string // Format: "HH:MM:SS" or "MM:SS"
  onChange: (value: string) => void
  onBlur?: () => void
  placeholder?: string
  error?: boolean
  showHours?: boolean // default true
}

const DurationPicker: React.FC<DurationPickerProps> = ({
  value,
  onChange,
  onBlur,
  placeholder = 'HH:MM:SS',
  error = false,
  showHours = true,
}) => {
  // Parse duration string to hours, minutes, seconds
  const { hours, minutes, seconds } = useMemo(() => {
    const parts = value.split(':').map((v) => parseInt(v, 10) || 0)

    if (showHours) {
      return {
        hours: parts[0] || 0,
        minutes: parts[1] || 0,
        seconds: parts[2] || 0,
      }
    } else {
      return {
        hours: 0,
        minutes: parts[0] || 0,
        seconds: parts[1] || 0,
      }
    }
  }, [value, showHours])

  const handleChange = (field: 'hours' | 'minutes' | 'seconds', val: number | null) => {
    const newVal = val || 0
    const formatted = showHours
      ? `${String(newVal).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
      : field === 'minutes'
        ? `${String(newVal).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
        : `${String(minutes).padStart(2, '0')}:${String(newVal).padStart(2, '0')}`

    onChange(formatted)
  }

  return (
    <div className={`duration-picker ${error ? 'error' : ''}`}>
      <Row gutter={8} align="middle">
        {showHours && (
          <>
            <Col span={6}>
              <div className="duration-input-wrapper">
                <InputNumber
                  className="duration-input"
                  min={0}
                  max={23}
                  value={hours}
                  onChange={(val) => {
                    if (showHours) {
                      const newVal = val || 0
                      onChange(
                        `${String(newVal).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
                      )
                    }
                  }}
                  onBlur={onBlur}
                  placeholder="00"
                />
                <label className="duration-label">Hours</label>
              </div>
            </Col>
            <Col span={1} className="duration-separator">
              :
            </Col>
          </>
        )}

        <Col span={showHours ? 6 : 8}>
          <div className="duration-input-wrapper">
            <InputNumber
              className="duration-input"
              min={0}
              max={59}
              value={minutes}
              onChange={(val) => {
                const newVal = val || 0
                if (showHours) {
                  onChange(
                    `${String(hours).padStart(2, '0')}:${String(newVal).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
                  )
                } else {
                  onChange(
                    `${String(newVal).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
                  )
                }
              }}
              onBlur={onBlur}
              placeholder="00"
            />
            <label className="duration-label">Minutes</label>
          </div>
        </Col>

        <Col span={1} className="duration-separator">
          :
        </Col>

        <Col span={showHours ? 6 : 8}>
          <div className="duration-input-wrapper">
            <InputNumber
              className="duration-input"
              min={0}
              max={59}
              value={seconds}
              onChange={(val) => {
                const newVal = val || 0
                if (showHours) {
                  onChange(
                    `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(newVal).padStart(2, '0')}`
                  )
                } else {
                  onChange(
                    `${String(minutes).padStart(2, '0')}:${String(newVal).padStart(2, '0')}`
                  )
                }
              }}
              onBlur={onBlur}
              placeholder="00"
            />
            <label className="duration-label">Seconds</label>
          </div>
        </Col>
      </Row>
    </div>
  )
}

export default DurationPicker
